import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { BloodPressure } from "@/Patient/Models/MedicalSummary/BloodPressure";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { ObstetricalVisit } from "@/Patient/Models/ObstetricalFile/ObstetricalVisit";
import { PrenatalVisitInitials } from "@/Patient/Models/ObstetricalFile/PrenatalVisitInitials";
import { BaseObstetricalFileSubSection, BaseObstetricalFileSubSectionProps } from "./BaseObstetricalFileSubSection";

interface IObstetricalFileSubsequentVisitsTableRowProps<TForm, TVisit extends ObstetricalVisit> extends BaseObstetricalFileSubSectionProps<TForm> {
    index: number;
    visit: TVisit;
    obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary;
    commentsId: string;
    onChange: (
        currentVisitChange: TVisit,
        shouldHandleFormUpdate?: boolean,
        isInitialsChange?: boolean
    ) => void;
    onSelectFromTextBtnClick: () => void;
    onVitalSignUpdated: () => void;
    vitalSignControlRenderer: ObstetricalFileControlRenderer;
    isLastRow: boolean;
}

abstract class ObstetricalFileSubsequentVisitsTableRow<TForm, TVisit extends ObstetricalVisit> extends BaseObstetricalFileSubSection<TForm, IObstetricalFileSubsequentVisitsTableRowProps<TForm, TVisit>, never> {
    protected readonly weightKey = "Weight";
    protected readonly bloodPressureKey = "BloodPressure";
    protected readonly dateKey = "Date";
    protected readonly gestationalAgeKey = "GestationalAge";
    protected readonly fetalHearthRateKey = "FetalHeartRate";
    protected readonly fetalMovementKey = "FetalMovement";
    protected readonly presentationKey = "Presentation";
    protected readonly commentsKey = "Comments";
    protected readonly nextVisitKey = "NextVisit";
    protected readonly initialsKey = "Initials";

    protected abstract get columnKeys(): string[];
    protected abstract get fieldLimits(): { [columnKey: string]: number };
    protected abstract get weightUnit(): WeightUnit;

    public componentDidMount(): void {
        this.setAcceptableLocales([Locale.en]);
    }

    public render(): JSX.Element {
        return (<tr>{this.columnKeys.map(key => this.renderCell(key))}</tr>);
    }

    protected renderCell(columnKey: string): JSX.Element {
        const visit = this.props.visit;
        const index = this.props.index;
        const isVitalSignsReadOnly = !this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(visit.vitalSign);

        switch (columnKey) {
            case this.weightKey: return (
                <td key={columnKey} className={!this.props.readonly && isVitalSignsReadOnly ? "not-allowed-hover" : ""}>
                    {this.props.vitalSignControlRenderer.renderTextbox({
                        value: visit.getConvertedWeight(this.weightUnit)?.toString(),
                        isReadOnlyOverride: isVitalSignsReadOnly,
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.setWeight(value && Number(value), this.weightUnit))),
                        className: `table-textbox ${visit.vitalSign?.emrIsInvalid ? "invalid-vital-sign" : ""}`,
                        isNumeric: true,
                        maxLength: this.fieldLimits[this.weightKey]
                    })}
                </td>
            );
            case this.bloodPressureKey: return (
                <td key={columnKey} className={!this.props.readonly && isVitalSignsReadOnly ? "not-allowed-hover" : ""}>
                    <BloodPressureField
                        cssClassName="visit-blood-pressure"
                        cssClassNameInput="blood-pressure-border"
                        readonly={this.props.readonly || isVitalSignsReadOnly}
                        bloodPressure={visit.vitalSign?.bloodPressure}
                        isInvalid={visit.vitalSign?.emrIsInvalid}
                        onBlur={() => this.props.onVitalSignUpdated()}
                        onBloodPressureChange={(bloodPressure: BloodPressure) => this.props.onChange(this.mutateVisit(v => v.BloodPressure = bloodPressure))}
                    />
                </td>
            );
            case this.dateKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderDateSelector({
                        value: visit.Date,
                        onChange: value => this.props.onChange(this.mutateVisitDate(v => v.Date = value), true),
                        id: `VisitDate_${index}`,
                        className: "table-date-selector",
                        isValid: true
                    })}
                </td>
            );
            case this.gestationalAgeKey: return (
                <td key={columnKey}>
                    <GestationalAgeComponent
                        id={`GestationWeeksAtBirth_${index}`}
                        gestationalAge={visit.GestationalAge}
                        readonly={this.props.readonly}
                        onChange={value => this.props.onChange(this.mutateVisit(v => v.GestationalAge = value))}
                        locale={Locale.en}
                        controlRenderer={this.controlRenderer}
                    />
                </td>
            );
            case this.fetalHearthRateKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderTextbox({
                        value: visit.FetalHeartRate?.toString(),
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.FetalHeartRate = value)),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.fetalHearthRateKey]
                    })}
                </td>
            );
            case this.fetalMovementKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderTextbox({
                        value: visit.FetalMovement,
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.FetalMovement = value)),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.fetalMovementKey]
                    })}
                </td>
            );
            case this.presentationKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderAutoCompleteTextbox({
                        value: visit.Presentation,
                        autoCompleteItems: ["∂", "9", "б", "ϱ", "C", cacheStore.GetString("BreechAbbrevLabel"), "T"].map(p => new SearchableItemModelT<string>(p, p, p)),
                        onAutoCompleteSelected: (code, model, value) => this.props.onChange(this.mutateVisit(v => v.Presentation = value)),
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.Presentation = value)),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.presentationKey],
                        disableMatchAutoSorting: true,
                        listWidth: "35px"
                    })}
                </td>
            );
            case this.commentsKey: return (
                <td key={columnKey} className="table-textarea comments">
                    {this.controlRenderer.renderResizableTextarea({
                        value: visit.Comments,
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.Comments = value)),
                        id: this.props.commentsId,
                        maxLength: 5000,
                        minHeight: "35px",
                        maxHeight: "112px",
                        containerCssClass: "resizable"
                    })}
                    {this.props.isLastRow && !this.props.readonly && (
                        <div className="text-right select-from-list-btn">
                            <a
                                onClick={e => {
                                    e.stopPropagation();
                                    this.props.onSelectFromTextBtnClick();
                                }}
                            >
                                {cacheStore.GetString("SelectFromListLabel")}
                            </a>
                        </div>
                    )}
                </td>
            );
            case this.nextVisitKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderTextbox({
                        value: visit.NextVisit,
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.NextVisit = value)),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.nextVisitKey]
                    })}
                </td>
            );
            case this.initialsKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderTextbox({
                        value: visit.Initials?.Value,
                        onChange: value => this.props.onChange(
                            this.mutateVisit(v => v.Initials = new PrenatalVisitInitials({ ...v.Initials, Value: value })),
                            false,
                            true
                        ),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.initialsKey]
                    })}
                </td>
            );
            default: return null;
        }
    }

    protected mutateVisit(mutate: (visit: TVisit) => void): TVisit {
        const visit = this.props.visit;
        mutate(visit);
        return visit;
    }

    protected mutateVisitDate(mutate: (visit: TVisit) => void): TVisit {
        return this.mutateVisit(v => {
            mutate(v);
        });
    }
}

export { IObstetricalFileSubsequentVisitsTableRowProps, ObstetricalFileSubsequentVisitsTableRow };
